
// React hooks used for memoized helpers, side effects, and local queue state.
import { useCallback, useEffect, useMemo, useState } from "react";

// API calls used by this hook to load and mutate queue items.
import {
  
  // Delete one queue item by job id.
  deleteAIGenerationQueueItem,

  // API call to load the AI generation queue.
  getAIGenerationQueue,

} from "api/APIStrichCalls";

// Shared confirmation dialog utility used before deleting queue items.
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";



// Custom hook to manage AI image generation queue
const useAiQueueManager = ({
  token,
  authReady,
  toastSuccess,
  toastError,

}) => {

  // Raw queue items from API and a flag used to control polling.
  const [images, setImages] = useState([]);

  // This flag controls whether polling is active.
  const [hasQueueItems, setHasQueueItems] = useState(false);

  // Fetch queue items and update polling state based on in-progress jobs.
  const fetchImages = useCallback(async () => {

    try {

      // Fetch the AI generation queue
      const queueResponse = await getAIGenerationQueue(token);

      // Get the queue items from the response, defaulting to an empty array if not present
      const queueImages = queueResponse.results || [];

      // Find in-progress items to determine whether polling should continue.
      const inProgressImages = queueImages.filter((img) => {

        // an item is in progress if it doesn't have a final image, error and had a progress < 100% 
        const isInProgress = !img?.image_url && Number(img?.progress) < 100;

        // return true for in-progress items, false for completed/failed items. This is used to determine if polling should continue.
        return isInProgress;

      });

      // Set polling to active if there are any in-progress items.
      setHasQueueItems(inProgressImages.length > 0);

      // Update state with the full queue.
      setImages(queueImages);

      // Return the queue images
      return queueImages;

    } catch (error) {
      
      // Log error to console and stop polling on error
      console.error("Error fetching image data:", error);
      
      // Stop polling
      setHasQueueItems(false);
      
      // Clear images state
      setImages([]);

      // Return empty array on error
      return [];

    }
  }, [token]);



  // Initial load once auth is ready.
  useEffect(() => {

    // wait for auth
    if (!authReady) return;
    
    // Fetch initial queue data
    fetchImages();

  }, [authReady, fetchImages]);



  // Poll every 2 seconds only while there are active queue jobs.
  useEffect(() => {

    // Don't start polling until auth is ready and there are queue items to track.
    if (!authReady || !hasQueueItems) return undefined;

    // Start polling interval
    const interval = setInterval(() => {

      // Fetch the latest queue data on each interval tick
      fetchImages();

    }, 2000);

    return () => clearInterval(interval);

  }, [authReady, hasQueueItems, fetchImages]);



  // Queue items shown in UI.
  const visibleImages = useMemo(() => {

    return images;

  }, [images]);



  // Stable identity used by popup/actions.
  const getQueueItemIdentity = useCallback((img) => {

    // use "job" as the unique identifier
    return String(img?.job || "");

  }, []);



  // In-progress means progress exists and there is no final output/error yet.
  const isInProgressQueueItem = useCallback((img) => {

    return typeof img?.progress === "number" && !img?.image_url && !img?.output && !img?.error_message;

  }, []);



  // Keep only items that are not currently in progress.
  // Completed, failed, and other non-running states are kept.
  const nonInProgressVisibleImages = useMemo(() => {

    return visibleImages.filter((img) => !isInProgressQueueItem(img));

  }, [isInProgressQueueItem, visibleImages]);



  // Queue-state check used for labels and progress display logic.
  const isQueueItemQueued = useCallback((img) => {

    const status = String(img?.status || "").toLowerCase();

    // Consider it "queued" if it has one of these words in the status
    return (
      status.includes("queue")
      || status.includes("pending")
      || status.includes("waiting")
      || (!img?.run_by && !status.includes("running"))
    );

  }, []);



  // Use countdown while queued (blue), otherwise use progress value (green).
  // Countdown = it's in a queue waiting to start
  // Progress = it's actively running on a card / server
  const getQueueItemProgressValue = useCallback((img) => {

    // If it has a valid countdown value, use that to show time until start.
    const countdown = Number(img?.countdown);

    if (Number.isFinite(countdown) && countdown > 0) return countdown;

    // Otherwise, show the progress percentage
    const progress = Number(img?.progress);

    // If it has a valid progress value, use that to show how close it is to completion.
    if (Number.isFinite(progress)) return progress;

    // Default to 0 if no valid progress or countdown value is available.
    return 0;

  }, []);



  // Choose color by phase:
  // - green when countdown has reached 0 (job is running/active)
  // - blue while countdown is still above 0 (still queued)
  const getQueueItemProgressColor = useCallback((img) => {

    // Read countdown from queue item.
    const countdown = Number(img?.countdown);
    
    // When countdown is exactly 0 use green, otherwise keep queued blue.
    return countdown === 0 ? "#00a43b" : "#007bff";

  }, []);



  // Convert any output value into a display string.
  // - This is used for showing data in the popup key/value display.
  const formatOutputValue = useCallback((value) => {

    // Handle null/undefined, primitives, arrays, and objects for display.
    if (value === null || value === undefined) return "";

    // For strings, numbers, and booleans, convert directly to string.
    if (typeof value === "string") return value;

    // For numbers and booleans, convert to string.
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    
    // For arrays, convert each item to string and join with commas.
    if (Array.isArray(value)) {

      return value.map((item) => (typeof item === "object" ? JSON.stringify(item) : String(item))).filter(Boolean).join(", ");

    }

    // For objects, convert to a pretty JSON string.
    return JSON.stringify(value);

  }, []);



  // Normalize output into key/value rows for popup rendering.
  // Handles plain strings, JSON strings, arrays, and objects.
  const getOutputEntries = useCallback((output) => {

    // Handle null/undefined/empty output gracefully. make it an empty array
    if (output === null || output === undefined || output === "") return [];

    // Start with the raw output value. 
    let parsed = output;

    // If it's a string, attempt to parse it as JSON. If parsing fails, treat it as a plain string output.
    if (typeof parsed === "string") {

      try {

        // Try to parse the string as JSON. If it succeeds, we will have a structured object or array to work with.
        parsed = JSON.parse(parsed);

      } catch {

        // If parsing fails, it's not JSON, so we treat the original string as the output value.
        return [{ key: "output", label: "Output", value: parsed }];

      }

    }

    // If it's an array, return each item as a separate entry. If it's an object, return each key/value pair as an entry.
    if (Array.isArray(parsed)) {

      // If it's an array, return each item as a separate entry with a label like "Item 1", "Item 2", etc.
      return parsed.map((item, index) => ({

          key: `output_${index}`,
          label: `Item ${index + 1}`,
          value: formatOutputValue(item),

        })).filter((item) => item.value.trim().length > 0);

    }

    // If it's an object, return each key/value pair as an entry.
    if (typeof parsed === "object") {

      // For objects, convert each key/value pair into an entry. The label is the key with underscores replaced by spaces for readability.
      return Object.entries(parsed).map(([key, value]) => ({

          key,
          label: key.replace(/_/g, " "),
          value: formatOutputValue(value),

        })).filter((item) => item.value.trim().length > 0);

    }

    // For primitive values (number, boolean) that were successfully parsed, convert to string and return as a single entry.
    return [{ key: "output", label: "Output", value: String(parsed) }];
    
  }, [formatOutputValue]);



  // Read "description" field from normalized output entries when present.
  // Used for "tag and describe" display in the UI.
  const getQueueItemOutputDescription = useCallback((img) => {

    // Get the normalized output entries for this queue item.
    const entries = getOutputEntries(img?.output);

    // Look for an entry with the key "description" 
    const descriptionEntry = entries.find((entry) => String(entry?.key || "").toLowerCase() === "description");

    // If found, return its value for display in the UI. Otherwise, return an empty string.
    return descriptionEntry?.value?.trim() || "";

  }, [getOutputEntries]);



  // Delete queue item after user confirmation, then refresh queue.
  const handleDeleteImage = useCallback((jobId, onDeleted) => {

    // Validate job ID before proceeding
    if (!jobId || String(jobId).trim().length === 0) {

      // Show error toast if job ID is missing or invalid
      toastError({ title: "Error", message: "Cannot delete queue item: missing job ID." });

      return;

    }

    // Show confirmation dialog before deleting
    DisplayConfirmationDialog(
      "<b>Are you sure?</b> <br />",
      "Delete",
      "Cancel",
      async (is_confirmed) => {

        // Cancel
        if (!is_confirmed) return;

        // Confirmed - proceed with deletion
        const response = await deleteAIGenerationQueueItem(token, jobId);

        if (response.success) {

          // Run callback if provided
          onDeleted?.();
          
          // Refresh queue to reflect deletion
          fetchImages();

          // Show success toast
          toastSuccess({ title: "Success", message: "Image deleted." });

        } else {

          // Show error toast on failure
          toastError({ title: "Error", message: "Could not delete image." });

        }

      }
    );

  }, [fetchImages, toastError, toastSuccess, token]);



  // Run a few refreshes after starting a workflow so new queue item appears quickly.
  const refreshQueueAfterWorkflowStart = useCallback(() => {

    // Immediately set hasQueueItems to true to start polling right away
    // then fetch images multiple times in the following seconds to quickly update the queue with the new item.
    setHasQueueItems(true);

    // Fetch images immediately
    fetchImages();

    // Fetch images a few more times in the following seconds to ensure the new queue item appears quickly in the UI.
    setTimeout(() => {

      fetchImages();

    }, 500);

    // Final fetch after 1 second to catch any items that might take a bit longer to appear in the queue.
    setTimeout(() => {

      fetchImages();

    }, 1000);

  }, [fetchImages]);

  return {

    images,
    visibleImages,
    nonInProgressVisibleImages,
    fetchImages,
    getQueueItemIdentity,
    isInProgressQueueItem,
    isQueueItemQueued,
    getQueueItemProgressValue,
    getQueueItemProgressColor,
    getOutputEntries,
    getQueueItemOutputDescription,
    handleDeleteImage,
    refreshQueueAfterWorkflowStart,

  };

};

export default useAiQueueManager;
